import React, { useState, useEffect, useCallback } from 'react';
// Assuming the path '../helper/apiCall' is correct
import apiCall from '../helper/apiCall'; 

// Utility function to generate a unique temporary ID for new items
const generateTempId = () => Date.now() + Math.random();

// Default values for a new line item
const initialItem = {
  id: generateTempId(),    // Client-side key/ID
  category_id: '',         // Corresponds to category master ID
  purchase_quantity: 1,
  purchase_price: 0.0,
  po_item_reference: null, // Placeholder for server-generated item reference
};

const PurchaseOrderForm = () => {
  const [manufacturerNames, setManufacturerNames] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]); 
  const [isLoading, setIsLoading] = useState(true);
  
  // State to track submission status and prevent double submission
  const [isSubmitting, setIsSubmitting] = useState(false); 
  
  // State to hold the final server-generated PO reference number
  const [poReferenceNumber, setPoReferenceNumber] = useState(null); 

  // State for Purchase Order Header (purchase_orders table)
  const [order, setOrder] = useState({
    manufacturer_id: '',
    purchase_date: new Date().toISOString().slice(0, 10), // YYYY-MM-DD
    discount: 0.0,
  });

  // State for Purchase Order Items (purchase_order_items table)
  const [items, setItems] = useState([initialItem]);


  // --- Data Fetching Functions ---

  // Fetches manufacturers using useCallback for dependency stability
  const fetchManufacturerNames = useCallback(async () => {
    try {
      const result = await apiCall('getManufacturerNames');
      // Assuming result.data is an array like: [{ manufacturer_id: 1, name: 'Alpha Corp' }, ...]
      setManufacturerNames(result.data);
    } catch (error) {
      console.error("Failed to fetch getManufacturerNames:", error);
    }
  }, []);

  // Fetches categories using useCallback for dependency stability
  const fetchCategoryOptions = useCallback(async () => {
    try {
      const result = await apiCall('getCategories');
      // Assuming result.data is an array like: [{ category_id: 201, name: 'Electronics' }, ...]
      setCategoryOptions(result.data);
    } catch (error) {
      console.error("Failed to fetch getCategories:", error);
    }
  }, []);

  // Effect to load initial data on component mount
  useEffect(() => {
    const loadInitialData = async () => {
        setIsLoading(true);
        // Load both data sources concurrently
        await Promise.all([
            fetchManufacturerNames(),
            fetchCategoryOptions(),
        ]);
        setIsLoading(false);
    };
    loadInitialData();
  }, [fetchManufacturerNames, fetchCategoryOptions]);


  // --- Calculations ---

  const calculateSubtotal = useCallback((items) => {
    return items.reduce((sum, item) => {
      const quantity = Number(item.purchase_quantity || 0);
      const price = Number(item.purchase_price || 0);
      return sum + (quantity * price);
    }, 0);
  }, []);

  const [subtotal, setSubtotal] = useState(0.0);
  const [totalAmount, setTotalAmount] = useState(0.0);

  // Effect to recalculate totals whenever items or discount change
  useEffect(() => {
    const newSubtotal = calculateSubtotal(items);
    setSubtotal(newSubtotal);
    const discountValue = Number(order.discount || 0);
    const newTotalAmount = newSubtotal - discountValue;
    setTotalAmount(Math.max(0, newTotalAmount));
  }, [items, order.discount, calculateSubtotal]);
  

  // --- Handlers ---

  const handleOrderChange = (e) => {
    const { name, value } = e.target;
    setOrder(prevOrder => ({
      ...prevOrder,
      [name]: name === 'discount' ? Number(value) : value,
    }));
  };

  const handleItemChange = (id, e) => {
    const { name, value } = e.target;
    setItems(prevItems => prevItems.map(item => {
      if (item.id === id) {
        // Convert category_id, quantity, and price to numbers
        const updatedValue = 
          name === 'category_id' || name === 'purchase_quantity' || name === 'purchase_price'
            ? Number(value) 
            : value;
            
        return {
          ...item,
          [name]: updatedValue,
        };
      }
      return item;
    }));
  };

  const handleAddItem = () => {
    setItems(prevItems => [...prevItems, { ...initialItem, id: generateTempId() }]);
  };

  const handleRemoveItem = (id) => {
    if (items.length > 1) {
      setItems(prevItems => prevItems.filter(item => item.id !== id));
    }
  };


  // --- Submission Logic ---

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Guard clause to prevent double submission
    if (isSubmitting) return;

    // Start submission process
    setIsSubmitting(true); 

    // Prepare data for the server (removes client-side 'id' and 'po_item_reference')
    const dataToSend = {
      ...order,
      total_amount: totalAmount.toFixed(2),
      // Map removes the client-side ID before sending
      purchase_order_items: items.map(({ id, po_item_reference, ...rest }) => rest),
    };

    console.log('Data to send to API for PO creation:', dataToSend);
    
    try {
        // Mock API Call: Assumes the API returns the created PO data, including reference numbers
        const response = await apiCall('createPurchaseOrder', dataToSend);
        
        const createdPO = response.data;
        
        // 1. Update the main PO reference number from the server response
        setPoReferenceNumber(createdPO.po_reference_number); 
        
        // 2. Update the items array with the server-generated item reference numbers
        const updatedItems = items.map((clientItem, index) => ({
            ...clientItem,
            po_item_reference: createdPO.purchase_order_items[index].po_item_reference,
        }));
        setItems(updatedItems);
        
        alert(`Purchase Order successfully created! Reference: ${createdPO.po_reference_number}`);

    } catch (error) {
        console.error("Failed to submit Purchase Order:", error);
        alert('Failed to submit Purchase Order. Check console for details.');
    } finally {
        // Re-enable the submit button regardless of success or failure
        setIsSubmitting(false); 
    }
  };
  
  // --- Loading State Display ---
  if (isLoading) {
    return (
        <div className="p-6 max-w-4xl mx-auto text-center text-xl text-indigo-600">
            Loading initial data (Manufacturers and Categories)...
        </div>
    );
  }

  // --- JSX Rendering ---
  return (
    <form onSubmit={handleSubmit} className="p-6 max-w-4xl mx-auto bg-white shadow-xl rounded-lg">
      <h2 className="text-3xl font-bold text-gray-800 mb-6 border-b pb-2">📦 New Purchase Order</h2>
      
      {/* Display Server-Assigned PO Number if available */}
      {poReferenceNumber && (
        <div className="mb-6 p-3 bg-indigo-50 border-l-4 border-indigo-500">
            <p className="text-lg font-semibold text-indigo-800">
                Purchase Order Reference: <span className="text-xl font-mono">{poReferenceNumber}</span>
            </p>
        </div>
      )}

      {/* --- Purchase Order Header Details --- */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div>
          <label htmlFor="manufacturer_id" className="block text-sm font-medium text-gray-700">
            Manufacturer
          </label>
          <select
            id="manufacturer_id"
            name="manufacturer_id"
            value={order.manufacturer_id}
            onChange={handleOrderChange}
            required
            // Disable inputs while submitting
            disabled={isSubmitting} 
            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-50"
          >
            <option value="">Select Manufacturer</option>
            {manufacturerNames.map(manufacturer => (
              <option 
                key={manufacturer.manufacturer_id} 
                value={manufacturer.manufacturer_id}
              >
                {manufacturer.manufacturer_name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="purchase_date" className="block text-sm font-medium text-gray-700">
            Purchase Date
          </label>
          <input
            type="date"
            id="purchase_date"
            name="purchase_date"
            value={order.purchase_date}
            onChange={handleOrderChange}
            required
            disabled={isSubmitting} 
            className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-50"
          />
        </div>
        <div className="md:col-span-1">
          <label htmlFor="discount" className="block text-sm font-medium text-gray-700">
            Discount 
          </label>
          <input
            type="number"
            id="discount"
            name="discount"
            min="0"
            step="0.01"
            value={order.discount}
            onChange={handleOrderChange}
            disabled={isSubmitting} 
            className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-50"
            placeholder="0.00"
          />
        </div>
      </div>

      {/* --- Purchase Order Items Table --- */}
      <h3 className="text-xl font-semibold text-gray-700 mb-4">Items Details</h3>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                #
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Item Ref.
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                Quantity
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                Price (Unit)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                Total
              </th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((item, index) => {
              const itemTotal = (item.purchase_quantity * item.purchase_price).toFixed(2);
              return (
                <tr key={item.id}>
                  <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {index + 1}
                  </td>
                  
                  {/* Category Select Dropdown */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <select
                      name="category_id"
                      value={item.category_id}
                      onChange={(e) => handleItemChange(item.id, e)}
                      required
                      disabled={isSubmitting} 
                      className="w-full border-gray-300 rounded-md shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50"
                    >
                      <option value="">Select Category</option>
                      {categoryOptions.map(category => (
                        <option 
                          key={category.category_id} 
                          value={category.category_id}
                        >
                          {category.category_name}
                        </option>
                      ))}
                    </select>
                  </td>
                  
                  {/* Display Item Reference Number */}
                  <td className="px-6 py-4 whitespace-nowrap text-xs font-mono text-gray-600">
                    {item.po_item_reference || 'TBD'}
                  </td>

                  {/* Quantity Input */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <input
                      type="number"
                      name="purchase_quantity"
                      min="1"
                      value={item.purchase_quantity}
                      onChange={(e) => handleItemChange(item.id, e)}
                      required
                      disabled={isSubmitting} 
                      className="w-full border-gray-300 rounded-md shadow-sm sm:text-sm disabled:bg-gray-50"
                    />
                  </td>

                  {/* Price Input */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <input
                      type="number"
                      name="purchase_price"
                      min="0.01"
                      step="0.01"
                      value={item.purchase_price}
                      onChange={(e) => handleItemChange(item.id, e)}
                      required
                      disabled={isSubmitting} 
                      className="w-full border-gray-300 rounded-md shadow-sm sm:text-sm disabled:bg-gray-50"
                    />
                  </td>

                  {/* Calculated Item Total */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    {itemTotal}
                  </td>

                  {/* Remove Button */}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(item.id)}
                      disabled={items.length === 1 || isSubmitting}
                      className={`text-red-600 hover:text-red-900 transition-colors 
                        ${items.length === 1 || isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        onClick={handleAddItem}
        disabled={isSubmitting} 
        className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
         Add Item Row
      </button>

      {/* --- Summary and Totals --- */}
      <div className="mt-8 pt-4 border-t border-gray-200 flex justify-end">
        <div className="w-full md:w-1/2 space-y-2">
          <div className="flex justify-between text-sm font-medium text-gray-600">
            <span>Subtotal:</span>
            <span>{subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm font-medium text-gray-600">
            <span>Discount:</span>
            <span>-{Number(order.discount).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-xl font-bold text-gray-900 pt-2 border-t">
            <span>Total Amount:</span>
            <span>{totalAmount.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* --- Submit Button --- */}
      <div className="mt-8 flex justify-end">
        <button
          type="submit"
          disabled={isSubmitting} 
          className={`w-full md:w-auto inline-flex justify-center py-3 px-6 border border-transparent shadow-sm text-base font-medium rounded-md text-white transition-colors 
            ${isSubmitting 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
            }`
          }
        >
          {isSubmitting 
            ? <> Submitting...</> 
            : 'Submit Purchase Order'
          }
        </button>
      </div>
    </form>
  );
};

export default PurchaseOrderForm;